// ✅ MIGRATED TO V2 DESIGN SYSTEM - Volvo/Teal Theme
// ✅ MIGRATED TO TanStack Query — Component → Hook → Service pattern
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, CheckCircle, Loader2, Eye, EyeOff, ExternalLink } from 'lucide-react';
import { notify } from '@/stores/notificationStore';
import { Button, Spinner, Badge } from '@/components/ui-v2';
import {
  useChorusProConfig,
  useSaveChorusProConfig,
  useTestChorusProConnection,
  useDisableChorusPro,
} from '../hooks/useChorusPro';

export const ChorusProSettings: React.FC = () => {
  const { t } = useTranslation();
  const [showClientSecret, setShowClientSecret] = useState(false);

  // TanStack Query hooks
  const { data: config, isLoading } = useChorusProConfig();
  const saveConfig = useSaveChorusProConfig();
  const testConnection = useTestChorusProConnection();
  const disableChorusPro = useDisableChorusPro();

  // Form state
  const [formData, setFormData] = useState({
    isEnabled: false,
    clientId: '',
    clientSecret: '',
    siret: '',
    environment: 'sandbox' as 'sandbox' | 'production',
  });

  // Sync form when config loads
  useEffect(() => {
    if (config) {
      setFormData({
        isEnabled: config.isEnabled || false,
        clientId: config.clientId || '',
        clientSecret: '',
        siret: config.siret || '',
        environment: (config.environment as 'sandbox' | 'production') || 'sandbox',
      });
    }
  }, [config]);

  // V2 Card classes
  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
    border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl
    transition-all duration-300 break-inside-avoid mb-6`;

  const inputClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
    focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
    hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
    transition-all duration-300 outline-none text-sm`;

  const handleSave = useCallback(() => {
    if (formData.isEnabled) {
      if (!formData.clientId || !formData.clientSecret) {
        notify.error('Client ID et Client Secret sont requis pour activer Chorus Pro', 'Erreur');
        return;
      }
    }
    saveConfig.mutate(formData);
  }, [formData, saveConfig]);

  const handleTestConnection = useCallback(() => {
    if (!formData.clientId || !formData.clientSecret) {
      notify.error('Client ID et Client Secret sont requis pour tester', 'Erreur');
      return;
    }
    testConnection.mutate({
      clientId: formData.clientId,
      clientSecret: formData.clientSecret,
      environment: formData.environment,
    });
  }, [formData, testConnection]);

  const handleDisable = useCallback(() => {
    disableChorusPro.mutate();
  }, [disableChorusPro]);

  if (isLoading) {
    return (
      <div className={cardClasses}>
        <div className="flex items-center justify-center py-8">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  const isConfigured = config?.isEnabled;
  const isSaving = saveConfig.isPending || disableChorusPro.isPending;
  const isTesting = testConnection.isPending;

  return (
    <div className={cardClasses}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-teal-100 rounded-xl">
          <FileText className="w-5 h-5 text-teal-600" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-gray-900">{t('compliance.chorusPro.title')}</h2>
          <p className="text-sm text-gray-500">{t('compliance.chorusPro.subtitle')}</p>
        </div>
        {isConfigured && (
          <Badge variant="success">{t('compliance.chorusPro.activated')}</Badge>
        )}
      </div>

      {/* Description */}
      <div className="bg-teal-50/50 backdrop-blur-sm border border-teal-200/50 rounded-2xl p-4 mb-6 hover:bg-teal-50/70 transition-all duration-300">
        <p className="text-sm text-teal-800">
          <strong>{t('compliance.chorusPro.title')}</strong> {t('compliance.chorusPro.description')}
        </p>
      </div>

      {/* Form */}
      <div className="space-y-4">
        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between p-4 bg-white/30 backdrop-blur-sm border border-white/40 rounded-2xl">
          <div>
            <label className="text-sm font-medium text-gray-700">
              {t('compliance.chorusPro.enable')}
            </label>
            <p className="text-xs text-gray-500 mt-1">
              {t('compliance.chorusPro.enableDescription')}
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isEnabled}
              onChange={(e) => setFormData({ ...formData, isEnabled: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
          </label>
        </div>

        {/* Environment Selection */}
        <div className="bg-white/30 backdrop-blur-sm border border-white/40 rounded-2xl p-4">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            {t('compliance.chorusPro.environment')}
          </label>
          <div className="flex gap-4">
            <label className="flex items-center cursor-pointer group">
              <input
                type="radio"
                value="sandbox"
                checked={formData.environment === 'sandbox'}
                onChange={(e) => setFormData({ ...formData, environment: e.target.value as 'sandbox' })}
                className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500"
              />
              <span className="ml-2 text-sm text-gray-700 group-hover:text-teal-600 transition-colors">{t('compliance.chorusPro.sandbox')}</span>
            </label>
            <label className="flex items-center cursor-pointer group">
              <input
                type="radio"
                value="production"
                checked={formData.environment === 'production'}
                onChange={(e) => setFormData({ ...formData, environment: e.target.value as 'production' })}
                className="w-4 h-4 text-teal-600 border-gray-300 focus:ring-teal-500"
              />
              <span className="ml-2 text-sm text-gray-700 group-hover:text-teal-600 transition-colors">{t('compliance.chorusPro.production')}</span>
            </label>
          </div>
        </div>

        {/* Client ID */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('compliance.chorusPro.clientId')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.clientId}
            onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
            className={inputClasses}
            placeholder={t('compliance.chorusPro.clientIdPlaceholder')}
          />
        </div>

        {/* Client Secret */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('compliance.chorusPro.clientSecret')} <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type={showClientSecret ? 'text' : 'password'}
              value={formData.clientSecret}
              onChange={(e) => setFormData({ ...formData, clientSecret: e.target.value })}
              className={`${inputClasses} pr-12`}
              placeholder={config?.clientId ? '••••••••' : t('compliance.chorusPro.clientSecretPlaceholder')}
            />
            <button
              type="button"
              onClick={() => setShowClientSecret(!showClientSecret)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-teal-600 transition-colors"
            >
              {showClientSecret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1.5">
            {t('compliance.chorusPro.clientSecretHidden')}
          </p>
        </div>

        {/* SIRET */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('compliance.chorusPro.siretOptional')}
          </label>
          <input
            type="text"
            value={formData.siret}
            onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
            className={inputClasses}
            placeholder="123 456 789 00010"
            maxLength={14}
          />
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <Button
            onClick={handleTestConnection}
            disabled={isTesting || isSaving || !formData.clientId || !formData.clientSecret}
            variant="secondary"
            icon={isTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            className="flex-1"
          >
            {isTesting ? t('compliance.chorusPro.testing') : t('compliance.chorusPro.testConnection')}
          </Button>

          <Button
            onClick={handleSave}
            disabled={isSaving || isTesting}
            icon={isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            className="flex-1"
          >
            {isSaving ? t('compliance.chorusPro.saving') : t('compliance.chorusPro.save')}
          </Button>
        </div>

        {/* Disable Button (only if enabled) */}
        {isConfigured && (
          <Button
            onClick={handleDisable}
            disabled={isSaving}
            variant="ghost"
            className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            {t('compliance.chorusPro.disable')}
          </Button>
        )}
      </div>

      {/* Info Footer */}
      <div className="mt-6 pt-4 border-t border-white/30">
        <p className="text-xs text-gray-500 flex flex-wrap items-center gap-1">
          <strong>{t('compliance.chorusPro.noteLabel')}:</strong> {t('compliance.chorusPro.infoFooter')}{' '}
          <a
            href="https://chorus-pro.gouv.fr"
            target="_blank"
            rel="noopener noreferrer"
            className="text-teal-600 hover:text-teal-700 hover:underline inline-flex items-center gap-1"
          >
            chorus-pro.gouv.fr
            <ExternalLink className="w-3 h-3" />
          </a>
          {' '}{t('compliance.chorusPro.infoFooterSuffix')}
        </p>
      </div>
    </div>
  );
};
